// lib/analytics.ts
//
// Client-side GA4 custom event tracking.
//
// Before this file existed, the only gtag() call anywhere in the codebase
// was the automatic pageview config in app/[locale]/layout.tsx. No tool ever
// reported that it actually did anything, so GA4 had nothing to offer as a
// "Key event" — every session looked identical whether the visitor converted
// a file or bounced immediately.
//
// One consistent event name (`tool_conversion`) is used for every tool
// rather than 22 bespoke event names, with `tool_name` as a parameter. That
// keeps GA4 Admin simple — mark `tool_conversion` as a single Key event once
// — while `tool_name` still lets you break results down per tool in reports.

import type { ToolName } from '@/components/ToolRenderer';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

// Tools with no file input anywhere in their UI (verified against each
// component's JSX — no `type="file"` element exists for these four): they
// operate purely on pasted/typed text. They still fire the GA4
// `tool_conversion` event below (that's a legitimate "did the tool do
// something" signal), but they must NOT feed the homepage "Files Processed"
// counter, since no file is ever involved. `base64-encoder-decoder` is
// deliberately excluded from this list — it has a real `type="file"` upload
// path in addition to its textarea, so a Base64 conversion sometimes does
// involve an actual file.
const NO_FILE_TOOLS: ReadonlySet<ToolName> = new Set<ToolName>([
  'word-counter',
  'json-formatter',
  'meta-tag-generator',
  'qr-code-generator',
]);

export interface ToolConversionParams {
  /**
   * Must match the tool's `slug` in data/tools.json (e.g. 'image-compressor') —
   * enforced at compile time via `ToolName` (see components/ToolRenderer.tsx),
   * which is keyed off the same 22 slugs. Never pass a landing-page/format
   * sub-slug here (e.g. 'jpg-to-webp', 'png-compressor', 'pdf-to-jpg') even on
   * SEO pages that lock a specific format — that distinction belongs in
   * `format` below, not `tool_name`, or GA4 fragments one tool into dozens of
   * rows instead of one.
   */
  tool_name: ToolName;
  /**
   * Optional: the actual input/output file format, e.g. 'jpg-to-webp', 'png',
   * 'gif', 'mp4', 'pdf'. This must be a file format only — never a quality
   * preset ('high'/'balanced'/'small') or a scale factor ('2', '4'). Tools
   * whose output format never varies (e.g. video-compressor always emits
   * mp4) should still pass that fixed format here rather than omitting it,
   * so `format` stays comparable across every row in GA4. Put anything else
   * that varies output — presets, scale factors — in `variant` instead.
   */
  format?: string;
  /**
   * Optional: a non-format variant of the conversion, e.g. a compression
   * preset ('high', 'screen') or an upscale factor ('2', '4'). Keeps
   * `format` reserved for actual file formats so cross-tool GA4 reports
   * broken down by `format` aren't mixing formats with presets/scales.
   */
  variant?: string;
  /** Optional: number of files processed in one action, when known. */
  file_count?: number;
}

/**
 * Fire when a tool successfully finishes producing output the user can
 * download/copy/use — i.e. the moment the tool actually delivered value.
 * Not on button click, not on upload — only on confirmed success, so this
 * can't be inflated by attempts that error out or get abandoned mid-way.
 *
 * Safe to call unconditionally: no-ops (with a console warning in dev) if
 * gtag hasn't loaded yet, e.g. an ad blocker stripped the GA script, or this
 * fires during a brief window before the script tag executes.
 */
export function trackToolConversion({ tool_name, format, variant, file_count }: ToolConversionParams): void {
  // Homepage "Files Processed" stat: a lightweight, best-effort beacon to our
  // own server-side counter (app/api/track/conversion), independent of
  // whether gtag/GA4 loaded. Fire-and-forget — a failed/blocked request here
  // should never affect the tool itself, so errors are swallowed silently.
  // Skipped entirely for NO_FILE_TOOLS (see above) so a "Files Processed"
  // stat can't be incremented by a tool that never touched a file. Passes
  // through file_count so a single batch action (e.g. merging 10 PDFs)
  // credits 10, not 1 — see lib/conversionCounter.ts for the full picture
  // of what this number does and does not prove.
  if (
    !NO_FILE_TOOLS.has(tool_name) &&
    typeof window !== 'undefined' &&
    typeof window.fetch === 'function'
  ) {
    window
      .fetch('/api/track/conversion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(typeof file_count === 'number' ? { file_count } : {}),
        keepalive: true,
      })
      .catch(() => {});
  }

  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[analytics] gtag not available, tool_conversion not sent:', tool_name);
    }
    return;
  }

  window.gtag('event', 'tool_conversion', {
    tool_name,
    ...(format ? { format } : {}),
    ...(variant ? { variant } : {}),
    ...(typeof file_count === 'number' ? { file_count } : {}),
  });
}

/**
 * Fire when the contact form is successfully submitted (API returned ok),
 * not on click of the Send button — same "only on confirmed success"
 * discipline as trackToolConversion, so a validation error or a failed
 * network request never counts as a submission.
 */
export function trackContactFormSubmit(): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[analytics] gtag not available, contact_form_submit not sent');
    }
    return;
  }

  window.gtag('event', 'contact_form_submit', {});
}

export interface ReviewSubmitParams {
  /** Which tool's page the review was left on, e.g. 'image-compressor'. */
  tool_slug: string;
  /** The star rating (1-5) the visitor chose. */
  rating: number;
}

/**
 * Fire when a review is successfully posted (API returned the created
 * review), not on click of the Submit button.
 */
export function trackReviewSubmit({ tool_slug, rating }: ReviewSubmitParams): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[analytics] gtag not available, review_submit not sent:', tool_slug);
    }
    return;
  }

  window.gtag('event', 'review_submit', { tool_slug, rating });
}

/**
 * Fire when the visitor toggles light/dark mode. `theme` is the mode they
 * switched TO, matching how GA4 users typically read a toggle event.
 */
export function trackThemeToggle(theme: 'light' | 'dark'): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[analytics] gtag not available, theme_toggle not sent:', theme);
    }
    return;
  }

  window.gtag('event', 'theme_toggle', { theme });
}

/**
 * Fire when the visitor picks a language from the switcher. `to_locale` is
 * the language they're navigating to; `from_locale` is what they were on.
 * Fired on click, before navigation completes — there's no "success"
 * signal to wait for since choosing the link *is* the action (unlike a
 * form POST that can fail server-side).
 */
export function trackLanguageSwitch(from_locale: string, to_locale: string): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[analytics] gtag not available, language_switch not sent:', to_locale);
    }
    return;
  }

  window.gtag('event', 'language_switch', { from_locale, to_locale });
}
